import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
const CATEGORIES = [{
  id: 'all',
  label: 'All Tools'
}, {
  id: 'general',
  label: 'General AI'
}, {
  id: 'retail',
  label: 'Retail Assistant'
}, {
  id: 'fashion',
  label: 'Fashion'
}, {
  id: 'price',
  label: 'Price Tracking'
}, {
  id: 'deals',
  label: 'Deals & Coupons'
}, {
  id: 'groceries',
  label: 'Groceries'
}, {
  id: 'food',
  label: 'Food Waste'
}, {
  id: 'expert',
  label: 'Human Expert'
}, {
  id: 'research',
  label: 'Research & Reviews'
}];
const TOOLS = [
// General AI
{
  id: 'chatgpt-shopping',
  name: 'ChatGPT Shopping Research & Instant Checkout',
  category: 'general',
  rating: 4.9,
  users: '2,000,000',
  desc: 'Personalized product research with detailed comparisons, citations, and instant checkout for select retailers.',
  tags: ['product research', 'comparison', 'checkout', 'ai assistant'],
  url: 'https://chatgpt.com'
}, {
  id: 'gemini-shopping',
  name: 'Gemini Shopping & AI Overviews',
  category: 'general',
  rating: 4.7,
  users: '1,500,000',
  desc: 'Shopping mode with product recommendations, visuals, prices, reviews, comparison tables, and instant checkout.',
  tags: ['google', 'product recommendations', 'comparison', 'visual search'],
  url: 'https://gemini.google.com'
}, {
  id: 'perplexity-shopping',
  name: 'Perplexity AI Shopping Mode',
  category: 'general',
  rating: 4.6,
  users: '800,000',
  desc: 'Natural language product search with context-rich recommendations, live data feeds, and source citations.',
  tags: ['conversational search', 'citations', 'research', 'product recommendations'],
  url: 'https://www.perplexity.ai'
}, {
  id: 'copilot-shopping',
  name: 'Copilot Shopping',
  category: 'general',
  rating: 4.5,
  users: '1,200,000',
  desc: 'Price comparison with product images, prices, ratings, review summaries, price history charts, and inline checkout.',
  tags: ['price comparison', 'product research', 'comparison'],
  url: 'https://copilot.microsoft.com'
},
// Retail Assistant
{
  id: 'amazon-rufus',
  name: 'Amazon Rufus & Buy For Me',
  category: 'retail',
  rating: 4.7,
  users: '5,000,000',
  desc: 'Conversational product search, price & deal checks, auto-add to cart, buy items at set price, convert handwritten lists.',
  tags: ['retail assistant', 'conversational search', 'price comparison'],
  url: 'https://www.amazon.com'
}, {
  id: 'klarna-assistant',
  name: 'Klarna AI Assistant',
  category: 'retail',
  rating: 4.8,
  users: '3,000,000',
  desc: 'Chat-based product search, category comparisons, personalized suggestions, and coupon auto-apply via browser extension.',
  tags: ['retail assistant', 'product search', 'deals & coupons'],
  url: 'https://www.klarna.com'
}, {
  id: 'bestbuy-gift',
  name: 'Best Buy Gift Finder',
  category: 'retail',
  rating: 4.6,
  users: '500,000',
  desc: 'AI chatbot that suggests gifts based on age and interests with interactive Q&A.',
  tags: ['retail assistant', 'gift finder', 'electronics'],
  url: 'https://www.bestbuy.com'
}, {
  id: 'target-bullseye',
  name: 'Target Bullseye Gift Finder',
  category: 'retail',
  rating: 4.5,
  users: '600,000',
  desc: 'Gift ideas based on age, hobbies & brands with product-page assistant that answers item questions.',
  tags: ['retail assistant', 'gift finder', 'product recommendations'],
  url: 'https://www.target.com'
}, {
  id: 'walmart-sparky',
  name: 'Walmart Sparky',
  category: 'retail',
  rating: 4.4,
  users: '1,000,000',
  desc: 'Occasion-based product suggestions, review synthesis, comparisons, with future multimodal support.',
  tags: ['retail assistant', 'product recommendations', 'comparison'],
  url: 'https://www.walmart.com'
},
// Fashion
{
  id: 'levis-stylist',
  name: "Levi's Outfitting & AI Stylist",
  category: 'fashion',
  rating: 4.7,
  users: '200,000',
  desc: 'Daily outfit generation based on inventory and purchase history with upcoming AI stylist chatbot.',
  tags: ['fashion', 'ai assistant', 'product recommendations'],
  url: 'https://www.levi.com'
}, {
  id: 'daydream-fashion',
  name: 'Daydream Fashion Chat',
  category: 'fashion',
  rating: 4.3,
  users: '150,000',
  desc: 'Conversational and photo-based search for fashion products with personalized recommendations.',
  tags: ['fashion', 'visual search', 'conversational search'],
  url: 'https://www.daydream.com'
}, {
  id: 'phia-price',
  name: 'Phia Price Comparison',
  category: 'fashion',
  rating: 4.2,
  users: '80,000',
  desc: 'Price-comparison platform tailored to celebrity and influencer outfits.',
  tags: ['fashion', 'price comparison', 'product search'],
  url: 'https://www.phia.com'
}, {
  id: 'oneoff-shopping',
  name: 'OneOff Shopping Assistant',
  category: 'fashion',
  rating: 4.1,
  users: '50,000',
  desc: 'Recommends products based on celebrity and creator outfits.',
  tags: ['fashion', 'product recommendations', 'ai assistant'],
  url: 'https://www.oneoff.com'
}, {
  id: 'acloset-wardrobe',
  name: 'Acloset Wardrobe Stylist',
  category: 'fashion',
  rating: 4.5,
  users: '300,000',
  desc: 'Digital wardrobe with AI stylist, daily outfit suggestions, and color and fit analysis.',
  tags: ['fashion', 'ai assistant', 'product recommendations'],
  url: 'https://www.acloset.ai'
},
// Price Tracking
{
  id: 'camelcamelcamel',
  name: 'CamelCamelCamel Price Tracker',
  category: 'price',
  rating: 4.8,
  users: '2,000,000',
  desc: 'Price drop alerts, price history charts, and Camelizer browser extension for Amazon.',
  tags: ['price tracking', 'price comparison', 'deals & coupons'],
  url: 'https://camelcamelcamel.com'
}, {
  id: 'keepa',
  name: 'Keepa Price Tracker',
  category: 'price',
  rating: 4.7,
  users: '1,500,000',
  desc: 'Embedded price history charts on Amazon pages with flexible alert settings and international price comparisons.',
  tags: ['price tracking', 'price comparison', 'product research'],
  url: 'https://keepa.com'
},
// Deals & Coupons
{
  id: 'paypal-honey',
  name: 'PayPal Honey',
  category: 'deals',
  rating: 4.6,
  users: '10,000,000',
  desc: 'Automatically applies coupon codes at checkout and earns PayPal Rewards points.',
  tags: ['deals & coupons', 'price comparison', 'shopping mode'],
  url: 'https://www.joinhoney.com'
},
// Groceries
{
  id: 'groceriesgpt',
  name: 'GroceriesGPT',
  category: 'groceries',
  rating: 4.3,
  users: '100,000',
  desc: 'Generates and manages grocery lists from text, receipts, and photos.',
  tags: ['groceries', 'ai assistant', 'product recommendations'],
  url: 'https://chatgpt.com/g/g-FCINlsXdK-groceriesgpt'
}, {
  id: 'ai-gen-grocery',
  name: 'AI-Gen.co Grocery Assistant',
  category: 'groceries',
  rating: 4.2,
  users: '50,000',
  desc: 'Grocery shopping assistant with meal planning and recipe suggestions.',
  tags: ['groceries', 'ai assistant', 'product recommendations'],
  url: 'https://chatgpt.com/g/g-JMtqHNNLn-grocery'
},
// Food Waste
{
  id: 'hellmanns-meal',
  name: "Hellmann's Meal Reveal",
  category: 'food',
  rating: 4.4,
  users: '200,000',
  desc: 'Scans fridge ingredients and suggests recipes via generative AI to reduce food waste.',
  tags: ['food waste', 'ai assistant', 'product recommendations'],
  url: 'https://www.hellmanns.com/us/en/meal-reveal.html'
},
// Human Expert
{
  id: 'curated-expert',
  name: 'Curated Expert Advice',
  category: 'expert',
  rating: 4.8,
  users: '400,000',
  desc: 'Matches users with human experts for specific gear with chat and personalized recommendations.',
  tags: ['human expert', 'product recommendations', 'ai assistant'],
  url: 'https://www.curated.com'
}, {
  id: 'bestbuy-expert',
  name: 'Best Buy Expert',
  category: 'expert',
  rating: 4.7,
  users: '800,000',
  desc: 'Live chat or video call with tech experts for personalized advice on electronics and appliances.',
  tags: ['human expert', 'electronics', 'product recommendations'],
  url: 'https://www.bestbuy.com/site/electronics/expert-service/pcmcat1528819595254.c'
}, {
  id: 'nordstrom-styling',
  name: 'Nordstrom Digital Styling',
  category: 'expert',
  rating: 4.8,
  users: '500,000',
  desc: 'Style quiz and curated outfit recommendations with virtual or in-store stylist sessions.',
  tags: ['human expert', 'fashion', 'product recommendations'],
  url: 'https://www.nordstrom.com/browse/services/personal-stylists'
}, {
  id: 'saks-stylist',
  name: 'Saks Fifth Avenue Text Stylist',
  category: 'expert',
  rating: 4.7,
  users: '150,000',
  desc: 'SMS-based concierge service for high-end shopping and styling.',
  tags: ['human expert', 'fashion', 'product recommendations'],
  url: 'https://www.saksfifthavenue.com'
},
// Research & Reviews
{
  id: 'cnet-reviews',
  name: 'CNET Reviews',
  category: 'research',
  rating: 4.6,
  users: '5,000,000',
  desc: 'Comprehensive news and reviews for tech products with independent testing.',
  tags: ['research', 'electronics', 'product research'],
  url: 'https://www.cnet.com'
}, {
  id: 'consumer-reports',
  name: 'Consumer Reports',
  category: 'research',
  rating: 4.9,
  users: '3,000,000',
  desc: 'Non-profit providing unbiased product ratings and buying advice with extensive testing since 1936.',
  tags: ['research', 'product research', 'comparison'],
  url: 'https://www.consumerreports.org'
}];
export function ShoppingToolsPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    document.title = 'AI Shopping Tools | ShoppingExpert.ai';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Discover 26 AI shopping advisors including ChatGPT, Gemini, Amazon Rufus, Klarna, and more. Find the perfect shopping assistant for your needs.');
    }
    window.scrollTo(0, 0);
  }, []);
  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return TOOLS.filter(tool => {
      const inCategory = activeCategory === 'all' || tool.category === activeCategory;
      const inQuery = !query || tool.name.toLowerCase().includes(query) || tool.desc.toLowerCase().includes(query) || tool.tags.some(tag => tag.toLowerCase().includes(query));
      return inCategory && inQuery;
    }).sort((a, b) => b.rating - a.rating);
  }, [activeCategory, searchQuery]);
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: TOOLS.length
    };
    TOOLS.forEach(tool => {
      counts[tool.category] = (counts[tool.category] || 0) + 1;
    });
    return counts;
  }, []);
  return <div className="min-h-screen bg-slate-50" data-id="element-1328">
      {/* Header */}
      <header className="sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-white/70 bg-white/90 border-b border-slate-200" data-id="element-1329">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4" data-id="element-1330">
          <div className="flex items-center justify-between" data-id="element-1331">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-1332">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4yJ3FscyPfrN1xAm7crLNW/ChatGPT_Image_Dec_2%2C_2025%2C_10_42_07_PM.png" alt="Shopping Expert.ai Logo" className="h-12 object-contain" data-id="element-1333" />
              <div data-id="element-1334">
                <span className="text-xl font-bold text-slate-900" data-id="element-1335">
                  AI Shopping Expert
                </span>
                <p className="text-xs text-slate-500" data-id="element-1336">AI Shopping Tools</p>
              </div>
            </Link>
            <nav className="hidden md:flex items-center gap-4 text-sm" data-id="element-1337">
              <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-1338">
                Home
              </Link>
              <Link to="/most-popular-ai-shopping-assistants" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-1339">
                Most Popular
              </Link>
              <Link to="/shopping-tools" className="text-slate-900 font-semibold border-b-2 border-slate-900 pb-1" data-id="element-1340">
                Shopping Tools
              </Link>
              <Link to="/productivity-tools" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-1341">
                Productivity
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-12" data-id="element-1342">
        {/* Hero Section */}
        <div className="text-center mb-12" data-id="element-1343">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-1344">
            AI Shopping Advisors
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8" data-id="element-1345">
            {TOOLS.length} tools found - Discover AI shopping assistants for
            product research, price comparison, fashion, groceries, and more.
          </p>

          {/* Search */}
          <div className="max-w-2xl mx-auto" data-id="element-1346">
            <input type="text" placeholder="Search by name, description, or tags..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-slate-300" data-id="element-1347" />
          </div>
        </div>

        {/* Categories */}
        <div className="mb-8" data-id="element-1348">
          <p className="text-sm font-semibold text-slate-700 mb-3" data-id="element-1349">
            Categories:
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-1350">
            {CATEGORIES.map(cat => <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={['rounded-full px-4 py-2 text-sm border transition-colors', activeCategory === cat.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'].join(' ')} data-id="element-1351">
                {cat.label} ({categoryCounts[cat.id] || 0})
              </button>)}
          </div>
        </div>

        {/* Tools List */}
        <div className="space-y-4" data-id="element-1352">
          {filtered.map(tool => <div key={tool.id} className="rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6" data-id="element-1353">
              <div className="flex items-start justify-between mb-3" data-id="element-1354">
                <div className="flex-1" data-id="element-1355">
                  <div className="flex items-center gap-3 mb-2" data-id="element-1356">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl" data-id="element-1357">
                      {tool.name.charAt(0)}
                    </div>
                    <div data-id="element-1358">
                      <h3 className="font-bold text-lg text-slate-900" data-id="element-1359">
                        {tool.name}
                      </h3>
                      <span className="inline-block px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200" data-id="element-1360">
                        {CATEGORIES.find(c => c.id === tool.category)?.label}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mb-3" data-id="element-1361">{tool.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-3" data-id="element-1362">
                    {tool.tags.map((tag, idx) => <span key={idx} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600" data-id="element-1363">
                        {tag}
                      </span>)}
                  </div>
                </div>
                <div className="text-right ml-4" data-id="element-1364">
                  <div className="flex items-center gap-1 text-lg font-bold text-slate-900 mb-1" data-id="element-1365">
                    <Star size={20} className="fill-yellow-400 text-yellow-400" data-id="element-1366" />
                    {tool.rating}
                  </div>
                  <p className="text-xs text-slate-500 mb-3" data-id="element-1367">
                    ({tool.users} users)
                  </p>
                  <a href={tool.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-semibold hover:bg-black transition-colors" data-id="element-1368">
                    Open Tool
                  </a>
                </div>
              </div>
            </div>)}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-16" data-id="element-1369">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-id="element-1370">
          <div className="text-center mb-4" data-id="element-1371">
            <p className="text-sm font-semibold text-slate-900 mb-1" data-id="element-1372">
              ShoppingExpert.ai
            </p>
            <p className="text-xs text-slate-600" data-id="element-1373">
              The independent directory of AI Shopping Agents & Tools.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500" data-id="element-1374">
            <div data-id="element-1375">© 2025 ShoppingExpert.ai • All rights reserved</div>
            <div className="flex items-center gap-3" data-id="element-1376">
              <Link to="/about" className="underline" data-id="element-1377">
                About
              </Link>
              <Link to="/faq" className="underline" data-id="element-1378">
                FAQ
              </Link>
              <Link to="/contact" className="underline" data-id="element-1379">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}